// src/contexts/EnvDataContext.js
import { createContext, useContext, useEffect, useState } from 'react';

const EnvDataContext = createContext();

export const EnvDataProvider = ({ children }) => {
  // devicesData จะเก็บข้อมูลล่าสุดของทุกอุปกรณ์, key คือ deviceId
  // เช่น { "ND-001": { id: "ND-001", timestamp: "...", sensorData: {...}, isActive: true, lastSeen: timestamp_ms }, ... }
  const [devicesData, setDevicesData] = useState({});
  const [rawMessages, setRawMessages] = useState([]); // เก็บ raw messages ล่าสุด (JSON string)

  // Effect สำหรับการเชื่อมต่อ WebSocket และรับข้อมูล
  useEffect(() => {
    // ตรวจสอบว่า REACT_APP_WEBSOCKET_URL มีค่าหรือไม่
    const websocketUrl = process.env.REACT_APP_WEBSOCKET_URL;
    if (!websocketUrl) {
      console.error("WebSocket URL is not defined. Please set REACT_APP_WEBSOCKET_URL.");
      // อาจจะ set error state หรือทำอย่างอื่นตามความเหมาะสม
      return;
    }

    const socket = new WebSocket(websocketUrl);

    socket.onopen = () => {
      console.log("WebSocket connected to:", websocketUrl);
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    socket.onmessage = (event) => {
      try {
        const rawText = event.data;
        const message = JSON.parse(rawText);

        // คาดหวัง format: { id: "device_id", timestamp: "iso_string", data: { pm25: ..., pm10: ..., ... } }
        const { id, timestamp, data: sensorDataObject } = message;

        if (!id || typeof sensorDataObject !== 'object' || sensorDataObject === null) {
          console.warn("Received WebSocket message with unexpected format:", message);
          return;
        }

        // ดึงค่าที่ต้องการจาก sensorDataObject, pm1 จะไม่ถูกดึงมาใช้งาน
        // ค่าอื่นๆ เช่น gas, temperature จะถูกเก็บไว้ใน otherSensorData
        const {
          pm25,
          pm10,
          tsp,
          noise,
          ...otherSensorData // เก็บค่าอื่นๆ ที่อาจมีใน data object
        } = sensorDataObject;

        setDevicesData(prevData => ({
          ...prevData,
          [id]: { // ใช้ id ของ device เป็น key
            id,
            timestamp,
            sensorData: { // เก็บข้อมูล sensor ทั้งหมดที่ parse ได้
              pm25: pm25 !== undefined ? parseFloat(pm25) : null,
              pm10: pm10 !== undefined ? parseFloat(pm10) : null,
              tsp: tsp !== undefined ? parseFloat(tsp) : null,
              noise: noise !== undefined ? parseFloat(noise) : null,
              ...otherSensorData // เพิ่ม field อื่นๆ ที่เหลือจาก data object
            },
            isActive: true, // เมื่อได้รับข้อมูลใหม่ ให้ถือว่า active
            lastSeen: Date.now() // เก็บเวลาที่ได้รับข้อมูลล่าสุด
          }
        }));

        // เก็บ raw message (JSON string) ล่าสุด 10 ข้อความ
        setRawMessages(prev => [rawText, ...prev.slice(0, 9)]);

      } catch (error) {
        console.error("Error processing WebSocket message:", error, "\nRaw data:", event.data);
      }
    };

    socket.onclose = (event) => {
      console.log("WebSocket disconnected.", event.reason ? `Reason: ${event.reason}` : '');
      // (Optional) เมื่อ WebSocket ปิดการเชื่อมต่อ อาจจะ mark ทุก device เป็น inactive
       setDevicesData(prevData => {
         const updatedData = { ...prevData };
         Object.keys(updatedData).forEach(deviceId => {
           updatedData[deviceId] = { ...updatedData[deviceId], isActive: false };
         });
         return updatedData;
       });
    };

    // Cleanup function: ปิด WebSocket connection เมื่อ component unmount
    return () => {
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
    };
  }, []); // Dependency array ว่างเปล่าเพื่อให้ effect นี้ทำงานครั้งเดียวตอน mount และ cleanup ตอน unmount

  // Effect สำหรับตรวจสอบสถานะ active ของแต่ละอุปกรณ์
  useEffect(() => {
    const ACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 นาที
    const CHECK_INTERVAL_MS = 30 * 1000; // ตรวจสอบทุกๆ 30 วินาที

    const activityCheckInterval = setInterval(() => {
      setDevicesData(prevData => {
        const now = Date.now();
        let hasChanges = false;
        const updatedData = { ...prevData }; // สร้าง copy เพื่อป้องกันการ mutate state โดยตรง

        for (const deviceId in updatedData) {
          // ตรวจสอบว่าเป็น property ของ object จริงๆ ไม่ใช่จาก prototype chain
          if (Object.prototype.hasOwnProperty.call(updatedData, deviceId)) {
            const device = updatedData[deviceId];
            // ถ้า device active อยู่ และไม่ได้รับข้อมูลนานเกิน ACTIVITY_TIMEOUT_MS
            if (device.isActive && (now - device.lastSeen > ACTIVITY_TIMEOUT_MS)) {
              updatedData[deviceId] = { ...device, isActive: false };
              hasChanges = true;
              console.log(`Device ${deviceId} marked as inactive due to timeout.`);
            }
          }
        }
        // อัปเดต state เฉพาะเมื่อมีการเปลี่ยนแปลง เพื่อลด re-render ที่ไม่จำเป็น
        return hasChanges ? updatedData : prevData;
      });
    }, CHECK_INTERVAL_MS);

    // Cleanup interval เมื่อ component unmount
    return () => {
      clearInterval(activityCheckInterval);
    };
  }, []); // Dependency array ว่างเปล่า

  return (
    <EnvDataContext.Provider value={{ devicesData, rawMessages }}>
      {children}
    </EnvDataContext.Provider>
  );
};

export const useEnvData = () => {
  const context = useContext(EnvDataContext);
  if (context === undefined) {
    throw new Error('useEnvData must be used within an EnvDataProvider');
  }
  return context;
};